/**
 * usePwaInstall — v3.21-1803
 *
 * Global PWA install helper.  Captures the browser's
 * `beforeinstallprompt` event AT MODULE LOAD (not inside a
 * component effect) so we never miss it if the browser fires it
 * before any component mounts.  Also detects standalone / iOS
 * installed state and listens for `appinstalled`.
 *
 * Contract:
 *   const { canPrompt, isInstalled, platform, promptInstall }
 *     = usePwaInstall();
 *
 *   canPrompt   — true iff a fresh, unused `BeforeInstallPromptEvent`
 *                 is currently held (native prompt is possible).
 *   isInstalled — true iff `display-mode:standalone` matches OR
 *                 `navigator.standalone === true` OR an
 *                 `appinstalled` event fired during this session.
 *                 Truth is derived from the browser — a stale
 *                 localStorage flag is never used.
 *   platform    — 'android' | 'ios' | 'desktop' | 'other'
 *   promptInstall() — returns { outcome, native, platform }
 *                     · 'accepted' | 'dismissed' | 'unavailable'
 *                     A 'dismissed' outcome does NOT mark the app as
 *                     installed — the button remains available for
 *                     any future `beforeinstallprompt` event.
 */
import { useEffect, useState, useCallback } from 'react';

// ── module-level singletons (captured before any component mounts)
let _deferred = null;
const _listeners = new Set();

function _notify() {
  _listeners.forEach((fn) => { try { fn(); } catch (_e) { /* noop */ } });
}

function _detectPlatform() {
  if (typeof navigator === 'undefined') return 'other';
  const ua = (navigator.userAgent || '').toLowerCase();
  const isIPad = /ipad/.test(ua)
    || (navigator.platform === 'MacIntel' && (navigator.maxTouchPoints || 0) > 1);
  if (isIPad || /iphone|ipod/.test(ua)) return 'ios';
  if (/android/.test(ua)) return 'android';
  return 'desktop';
}

function _detectInstalled() {
  if (typeof window === 'undefined') return false;
  try {
    if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return true;
  } catch (_e) { /* noop */ }
  try {
    if (typeof navigator !== 'undefined' && navigator.standalone === true) return true;
  } catch (_e) { /* noop */ }
  return false;
}

if (typeof window !== 'undefined' && !window.__hubPwaInstallBound) {
  window.__hubPwaInstallBound = true;
  window.addEventListener('beforeinstallprompt', (e) => {
    // Retain the deferred event so a click on the footer button can
    // invoke prompt() later even if the browser fired this event
    // long before the user reached the footer.
    try { e.preventDefault(); } catch (_e) { /* noop */ }
    _deferred = e;
    _notify();
  });
  window.addEventListener('appinstalled', () => {
    _deferred = null;
    _notify();
  });
}

export default function usePwaInstall() {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const fn = () => setTick((t) => t + 1);
    _listeners.add(fn);
    return () => { _listeners.delete(fn); };
  }, []);

  const canPrompt = !!_deferred;
  const isInstalled = _detectInstalled();
  const platform = _detectPlatform();

  const promptInstall = useCallback(async () => {
    if (!_deferred) return { outcome: 'unavailable', native: false, platform };
    try {
      const ev = _deferred;
      // A given BeforeInstallPromptEvent may only be prompted once.
      _deferred = null;
      _notify();
      await ev.prompt();
      const choice = await ev.userChoice;
      // If dismissed, the browser will typically re-fire
      // beforeinstallprompt later; do NOT persist a "dismissed" flag.
      return { outcome: choice?.outcome || 'dismissed', native: true, platform };
    } catch (_e) {
      return { outcome: 'unavailable', native: false, platform };
    }
  }, [platform, tick]);

  return { canPrompt, isInstalled, platform, promptInstall };
}
